import React, { useState } from 'react';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import { useAppContext } from '../contexts/AppContext';
import {
    generatePDiagram,
    deriveSystemFunctions,
    generateFmeaDrafts,
    generateDesignOptimizations,
    conductMarketResearch,
    findSimilarProducts
} from '../services/geminiService';
import { FmeaItem } from '../types';
import { Sparkles, Save, CheckSquare, Square, AlertTriangle, RefreshCw, Zap, Target, Search, TrendingUp, Box } from 'lucide-react';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { CostDashboard } from '../components/CostDashboard';
import { SynthesisTelemetry } from '../components/SynthesisTelemetry';
import { ApprovalModal } from '../components/ApprovalModal';
import { agentManager } from '../services/agentManager';

import { ProjectContextModal, ProjectContext } from '../components/ProjectContextModal';
import { MagnusGenesisWizard, AnalysisConfig } from '../components/MagnusGenesisWizard';
import CadViewerModal from '../components/CadViewerModal';

const MagnusGenesisPage: React.FC = () => {
    const { allData, updateFmeaData, addEpoch, addPDiagram, masterFunctions, navigate, apiMode, setApiMode, updateProjectContext, activeProject, importProjectData } = useAppContext();

    // Context State
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [projectContext, setProjectContext] = useState<ProjectContext | null>(null);

    // Simulation State
    const [currentPhase, setCurrentPhase] = useState<number>(0);
    const [isRunning, setIsRunning] = useState(false);

    // CAD Viewer State — wired to CadViewerModal
    const [activeCadSynthesisId, setActiveCadSynthesisId] = useState<{id: string, name: string} | null>(null);
    const [cadAvailable, setCadAvailable] = useState(false);
    const [lastSynthesisId, setLastSynthesisId] = useState<string>('');
    const [v5Telemetry, setV5Telemetry] = useState<any>(null);

    const handleStartClick = () => {
        setShowWizard(true); // Open wizard instead of old modal
    };

    const handleWizardStart = (config: AnalysisConfig) => {
        // Convert wizard config to project context
        const context: ProjectContext = {
            projectName: config.productCategory,
            description: `Analysis from ${config.inputSource}`,
            requirements: config.requirements.map(r => r.requirementText),
            metadata: {
                id: `PROJ-${Date.now()}`,
                name: config.productCategory,
                created: new Date().toISOString(),
                lastModified: new Date().toISOString(),
                version: '1.0.0',
                author: 'Current User',
                description: `Generated from ${config.inputSource}: ${config.sourceName || 'manual entry'}`
            }
        };

        setProjectContext(context);
        setShowWizard(false);
        run7Epochs({ renderCad: config.renderCad, tempContext: context });
    };

    const handleProjectStart = (context: ProjectContext) => {
        setProjectContext(context);

        // Persist to Global Unified Context
        updateProjectContext({
            id: `PROJ-${Date.now()}`,
            name: context.projectName,
            description: context.description,
            created: new Date().toISOString(),
            lastModified: new Date().toISOString(),
            version: '1.0.0',
            author: 'Current User'
        }, {
            customerName: 'Default Customer',
            productName: context.projectName,
            productDescription: context.description,
            objectives: context.requirements || [],
        });

        setShowProjectModal(false);
        run7Epochs({ tempContext: context });
    };

    const [logs, setLogs] = useState<string[]>([]);
    const [researchReport, setResearchReport] = useState<any>(allData.opalInsight || null);
    const [similarProducts, setSimilarProducts] = useState<any[]>([]);
    const [derivedFunctions, setDerivedFunctions] = useState<any[]>(allData.derivedFunctions || []);
    const [generatedPDiagrams, setGeneratedPDiagrams] = useState<any[]>(allData.pDiagramList || []);
    const [fmeaResults, setFmeaResults] = useState<any[]>(allData.fmeaItems || []);
    const [optimizations, setOptimizations] = useState<any[]>([]);
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

    // SYNC: Load from allData if available (Persistence v2.0.0)
    React.useEffect(() => {
        if (allData.fmeaItems?.length > 0) {
            setFmeaResults(allData.fmeaItems);
            // Only reset selection if it's the first load or if sizes changed significantly
            if (fmeaResults.length === 0) {
                setSelectedIndices(new Set(allData.fmeaItems.map((_, i) => i)));
            }
        }
        if (allData.pDiagramList?.length > 0) {
            setGeneratedPDiagrams(allData.pDiagramList);
        }
        if (allData.derivedFunctions?.length > 0) {
            setDerivedFunctions(allData.derivedFunctions);
        }
        if (allData.opalInsight) {
            setResearchReport(allData.opalInsight);
        }
    }, [allData]);

    // Stop Simulation Ref
    const stopRef = React.useRef(false);

    // Evolution Tracking
    const [evolutionStats, setEvolutionStats] = useState<{
        currentEpoch: number;
        totalEpochs: number;
        startRPN: number;
        currentRPN: number;
        totalItems: number;
        history: { epoch: number; rpn: number; items: number }[];
    }>({
        currentEpoch: 0,
        totalEpochs: 7,
        startRPN: 0,
        currentRPN: 0,
        totalItems: 0,
        history: []
    });
    
    // Agent Governance State (Extended Swarm — SIPOC V4)
    const [activeAgents, setActiveAgents] = useState<any[]>([
        { id: 'research_agent', name: 'Technical Researcher', isActive: true, icon: <Search size={16} /> },
        { id: 'functional_agent', name: 'Functional Architect', isActive: true, icon: <Zap size={16} /> },
        { id: 'geometry_agent', name: 'Geometry & CAD Engineer', isActive: true, icon: <Target size={16} /> },
        { id: 'requirement_mapper', name: 'RTM Specialist (L5.5)', isActive: true, icon: <CheckSquare size={16} /> },
        { id: 'fmea_agent', name: 'Reliability Engineer (FMEA)', isActive: true, icon: <AlertTriangle size={16} /> },
        { id: 'fta_agent', name: 'Fault Tree Analyst (L6.5)', isActive: true, icon: <AlertTriangle size={16} /> },
        { id: 'optimization_agent', name: 'RPN Optimizer (L6.7)', isActive: true, icon: <TrendingUp size={16} /> },
        { id: 'material_expert', name: 'Material Scientist', isActive: true, icon: <TrendingUp size={16} /> },
        { id: 'lifecycle_agent', name: 'Lifecycle & MTBF', isActive: true, icon: <TrendingUp size={16} /> },
        { id: 'dvp_agent', name: 'DVP&R Test Planner (L14.5)', isActive: true, icon: <CheckSquare size={16} /> },
        { id: 'interface_agent', name: 'Electrical Topology (L15.5)', isActive: true, icon: <Zap size={16} /> },
        { id: 'physics_agent', name: 'Buckingham-Pi Physics', isActive: true, icon: <Zap size={16} /> }
    ]);

    React.useEffect(() => {
        const fetchAgents = async () => {
            try {
                const res = await authenticatedFetchJSON('/api/v1/genesis/agents');
                if (res && res.agents) {
                    setActiveAgents(res.agents);
                }
            } catch (err) {
                console.warn("Failed to fetch full agent registry:", err);
            }
        };
        fetchAgents();
    }, []);


    const toggleAgent = (id: string) => {
        setActiveAgents(prev => prev.map(a => a.id === id ? { ...a, isActive: !a.isActive } : a));
    };

    const loadGovernanceScenario = (scenario: string) => {
        let activeIds: string[] = [];
        switch(scenario) {
            case 'S-01_FULL':
            case 'S-08_SWARM':
                activeIds = activeAgents.map(a => a.id);
                break;
            case 'S-03_RISK':
                activeIds = ['fmea_agent'];
                break;
            case 'S-04_BASELINE':
                activeIds = [];
                break;
            case 'S-05_DESIGN':
                activeIds = ['research_agent', 'geometry_agent'];
                break;
            case 'S-06_MATERIAL':
                activeIds = ['material_expert'];
                break;
            case 'S-07_LIFECYCLE':
                activeIds = ['lifecycle_agent'];
                break;
            default:
                activeIds = activeAgents.map(a => a.id);
        }
        
        setActiveAgents(prev => prev.map(a => ({
            ...a,
            isActive: activeIds.includes(a.id)
        })));
        addLog(`📂 Governance Protocol Engaged: ${scenario}`);
    };

    // System Health / Self-Test State
    type HealthStatus = 'pending' | 'ok' | 'fail' | 'warning';
    const [healthChecks] = useState<{ [key: string]: { status: HealthStatus, msg: string } }>({
        api_connection: { status: 'pending', msg: 'Waiting...' },
        context_load: { status: 'pending', msg: 'Waiting...' },
        research_status: { status: 'pending', msg: 'Waiting...' },
        generation_status: { status: 'pending', msg: 'Waiting...' },
        growth_control: { status: 'pending', msg: 'Waiting...' }
    });

    // Options
    const [enablePhysics, setEnablePhysics] = useState<boolean>(false); // ⚡ PERFORMANCE: Disabled by default (saves ~42-63s total) // Default to true if user wants to see it, but can toggle off

    // const updateHealth = (key: string, status: HealthStatus, msg: string) => {
    //     setHealthChecks(prev => ({ ...prev, [key]: { status, msg } }));
    // };

    const customerRequirements = allData.customerRequirements || [];

    const addLog = (msg: string) => setLogs(prev => [...prev, msg]);

    const stopSimulation = () => {
        stopRef.current = true;
        addLog("🛑 STOP REQUESTED: Halting simulation after current step...");
    };

    const runCompleteWorkflow = async (
        epochOverride?: number,
        customRequirements?: any[],
        globalKnowledge?: any[]
    ): Promise<{
        success: boolean;
        fmeaItems: any[];
        pDiagrams: any[];
        functions: any[];
        error?: string
    }> => {
        setIsRunning(true);
        setCurrentPhase(0);

        // Only clear logs if it's a fresh single run, not part of a batch
        if (!epochOverride) {
            setLogs([]);
            setResearchReport(null);
            setSimilarProducts([]);
            setDerivedFunctions([]);
            setGeneratedPDiagrams([]);
            setFmeaResults([]);
            setOptimizations([]);
        }

        // Use custom requirements if provided (from context), otherwise fallback to global store
        const activeRequirements = customRequirements || allData.customerRequirements || [];

        // Config object for services
        const serviceOptions = { apiMode: 'backend' as const };

        try {
            if (stopRef.current) throw new Error("Simulation stopped by user.");

            // Initialize agents with budget
            const targetBudget = 50.00; // Default budget, could be from requirements
            if (!epochOverride) addLog(`💰 Initializing agents with budget: $${targetBudget.toFixed(2)}`);
            agentManager.initializeForEpoch(targetBudget);

            if (!epochOverride) {
                addLog(`🤖 Using AI Mode: ${apiMode.toUpperCase()}`);
                if (globalKnowledge && globalKnowledge.length > 0) {
                    addLog(`🧠 Global Knowledge Active: Access to ${globalKnowledge.length} historical failure modes.`);
                }
            }

            await new Promise(r => setTimeout(r, 500));

            // PHASE 0: Market Research & Competitive Intelligence (NEW!)
            setCurrentPhase(0);
            if (!epochOverride) addLog("🔍 [Phase 0] Research Agent: Conducting market research...");

            // Infer product category from requirements
            const productCategory = activeRequirements.length > 0
                ? `${activeRequirements[0].requirementText.split(' ').slice(0, 3).join(' ')} device`
                : "consumer appliance";

            if (!epochOverride) addLog(`📊 Researching: "${productCategory}"`);

            let localResearchReport = null;
            try {
                if (stopRef.current) throw new Error("Simulation stopped by user.");
                const research = await conductMarketResearch(productCategory, customerRequirements, serviceOptions);
                localResearchReport = research;
                setResearchReport(research);

                // Logging only for single run to reduce noise in batch
                if (!epochOverride) {
                    if (research.competitiveProducts?.length > 0) addLog(`✓ Found ${research.competitiveProducts.length} competitive products`);
                    if (research.failureModes?.length > 0) addLog(`✓ Identified ${research.failureModes.length} common failure modes from market`);
                    if (research.bestPractices?.length > 0) addLog(`✓ Extracted ${research.bestPractices.length} best practices`);
                    if (research.standards?.length > 0) addLog(`✓ Found ${research.standards.length} applicable safety standards`);
                }

                // Search for similar products in our database (Client-side mainly for now)
                const similar = await findSimilarProducts(
                    customerRequirements,
                    allData.iterationHistory || []
                );
                setSimilarProducts(similar);

                if (!epochOverride && similar.length > 0) {
                    addLog(`💡 Found ${similar.length} similar products in database (${similar[0].similarityScore}% match)`);
                }

            } catch (researchError: any) {
                if (researchError.message === "Simulation stopped by user.") throw researchError;
                addLog(`⚠️ Research phase encountered issues: ${researchError.message}`);
                addLog("→ Proceeding with available data...");
            }

            await new Promise(r => setTimeout(r, 1000));

            // PHASE 1: Requirements Discovery
            setCurrentPhase(1);
            if (!epochOverride) addLog("📋 [Phase 1] Customer Requirements Discovery");
            if (!epochOverride) addLog(`✓ Loaded ${customerRequirements.length} customer requirements`);
            await new Promise(r => setTimeout(r, 200)); // ⚡ PERFORMANCE: Reduced from 800ms

            // PHASE 2: Function Derivation (Enhanced with research)
            setCurrentPhase(2);
            if (!epochOverride) addLog("🔧 [Phase 2] Functional Analysis Agent: Deriving functions...");

            if (stopRef.current) throw new Error("Simulation stopped by user.");

            if (localResearchReport?.rawResearch && !epochOverride) {
                addLog("→ Using market research to inform function derivation");
            }

            const functions = await deriveSystemFunctions(
                activeRequirements.map(r => ({ id: r.id, text: r.requirementText, type: r.type })),
                serviceOptions
            );

            setDerivedFunctions(functions);
            if (!epochOverride) addLog(`✓ Identified ${functions.length} core functions`);
            if (!epochOverride) functions.forEach(f => addLog(`  • ${f.functionName} → ${f.relatedRequirements.join(', ')}`));
            await new Promise(r => setTimeout(r, 1000));

            // PHASE 3: P-Diagram Generation (Enhanced with failure insights)
            setCurrentPhase(3);
            if (!epochOverride) addLog("📐 [Phase 3] P-Diagram Synthesis: Generating diagrams...");

            if (stopRef.current) throw new Error("Simulation stopped by user.");

            if (localResearchReport?.failureModes?.length > 0 && !epochOverride) {
                addLog("→ Incorporating known failure modes from market research");
            }

            const pDiagrams: any[] = [];
            for (const func of functions.slice(0, 3)) {
                if (stopRef.current) throw new Error("Simulation stopped by user.");

                // SAFETY CHECK: Ensure function object is valid
                if (!func || !func.relatedRequirements || !Array.isArray(func.relatedRequirements)) {
                    addLog(`⚠️ Skipping invalid function response: ${JSON.stringify(func)}`);
                    continue;
                }

                const relatedReqs = activeRequirements.filter(r => func.relatedRequirements.includes(r.id));
                let systemDesc = `Function: ${func.functionName}\nDescription: ${func.description}\nRequirements: ${relatedReqs.map(r => r.requirementText).join('; ')}`;

                // Enhance with research data
                if (localResearchReport?.failureModes) {
                    systemDesc += `\n\nKNOWN FAILURES IN MARKET (to avoid): ${localResearchReport.failureModes.slice(0, 3).map((f: any) => f.mode || f).join(', ')}`;
                }
                if (localResearchReport?.bestPractices) {
                    systemDesc += `\n\nBEST PRACTICES: ${localResearchReport.bestPractices.slice(0, 3).join(', ')}`;
                }

                const pDiagram = await generatePDiagram(systemDesc, serviceOptions);
                pDiagram.consolidatedFunctionId = func.functionId;
                pDiagram.systemName = func.functionName;
                pDiagrams.push(pDiagram);

                if (!epochOverride) addLog(`✓ P-Diagram for "${func.functionName}"`);
                await new Promise(r => setTimeout(r, 100)); // ⚡ PERFORMANCE: Reduced from 500ms
            }
            setGeneratedPDiagrams(pDiagrams);

            // Continue with PHASE 4, 5 as before...
            // (rest of the implementation remains the same)

            // PHASE 4: FMEA Ideation
            setCurrentPhase(4);
            if (!epochOverride) addLog("⚠️ [Phase 4] FMEA Ideation: Generating failure modes...");

            if (stopRef.current) throw new Error("Simulation stopped by user.");

            let allFmeaItems: any[] = [];
            const allFunctions = Array.from(new Set([...masterFunctions, ...functions.map(f => f.functionId)]));

            for (const pDiagram of pDiagrams) {
                if (stopRef.current) throw new Error("Simulation stopped by user.");
                const func = functions.find(f => f.functionId === pDiagram.consolidatedFunctionId);
                const relatedReqs = activeRequirements.filter(r => func?.relatedRequirements.includes(r.id));

                const data = await generateFmeaDrafts(
                    pDiagram,
                    allData.fmeaItems,
                    allData.controls,
                    allData.dvprTests,
                    allFunctions,
                    relatedReqs,
                    10,
                    globalKnowledge || [],
                    serviceOptions
                );

                allFmeaItems = [...allFmeaItems, ...data.newFmeaItems];
                if (!epochOverride) addLog(`✓ ${data.newFmeaItems.length} failure modes for ${pDiagram.systemName}`);
            }

            const fmeaWithRpn = allFmeaItems.map(item => ({
                ...item,
                RPN: (item.S || 1) * (item.O || 1) * (item.D || 1)
            })).sort((a, b) => (b.RPN || 0) - (a.RPN || 0));

            setFmeaResults(fmeaWithRpn);
            setSelectedIndices(new Set(fmeaWithRpn.map((_, i) => i)));
            if (!epochOverride) addLog(`✓ Total: ${fmeaWithRpn.length} items (Highest RPN: ${fmeaWithRpn[0]?.RPN})`);

            // PHASE 4.5: Physical Validation (Physics Agent)
            if (enablePhysics && !epochOverride && apiMode === 'backend') {
                addLog("⚙️ [Physics Agent] Validating mechanical constraints & CadQuery geometry...");

                // Validate top 3 items (High RPN)
                for (const item of fmeaWithRpn.slice(0, 3)) {
                    try {
                        // Normalize causes
                        let causesStr = "Unknown";
                        if (Array.isArray(item.causes)) causesStr = item.causes.join(', ');
                        else if (typeof item.causes === 'string') causesStr = item.causes;

                        const valRes = await authenticatedFetchJSON('/api/agents/physics/validate-mechanism', {
                            method: 'POST',
                            body: JSON.stringify({
                                item: item.itemFunction || item.function || "System",
                                failureMode: item.failureMode,
                                causes: causesStr,
                                effects: item.effects || "Unknown"
                            })
                        });

                        // Logic updated to assume valRes IS the JSON now
                        const critique = valRes;
                        if (!epochOverride) addLog(`  • ${item.failureMode.substring(0, 25)}... → ${critique.verdict}`);

                        // Attach critique to item notes for the report
                        item.notes = (item.notes || "") + `\n[Physics Agent]: ${critique.verdict}\n[Reasoning]: ${critique.reasoning}`;
                        if (critique.cad_check) {
                            item.notes += `\n[CadQuery]: ${critique.cad_check}`;
                        }
                    } catch (e) {
                        console.warn("Physics validation failed", e);
                    }
                }
            }

            // PHASE 5: Optimization Analysis
            setCurrentPhase(5);
            if (!epochOverride) addLog("🎯 [Phase 5] Optimization Agents: Analyzing improvements...");

            if (stopRef.current) throw new Error("Simulation stopped by user.");

            const opts = await generateDesignOptimizations(
                fmeaWithRpn.map(item => ({ ...item, id: `TEMP-${Date.now()}` } as FmeaItem)),
                pDiagrams[0],
                activeRequirements,
                serviceOptions
            );

            setOptimizations(opts);
            if (!epochOverride) addLog(`✓ Generated ${opts.length} optimization proposals`);
            if (!epochOverride) opts.slice(0, 3).forEach(o => addLog(`  • ${o.proposedAction} (Expected RPN reduction)`));

            setCurrentPhase(6);
            if (!epochOverride) addLog("✅ [Complete] Workflow finished - Ready to save Epoch");

            // RETURN DATA FOR BATCH PROCESSING
            return {
                success: true,
                fmeaItems: fmeaWithRpn,
                pDiagrams: pDiagrams,
                functions: functions
            };

        } catch (e: any) {
            addLog(`❌ Error: ${e.message}`);
            return {
                success: false,
                fmeaItems: [],
                pDiagrams: [],
                functions: [],
                error: e.message
            };
        } finally {
            if (!epochOverride) setIsRunning(false); // Only turn off running if single run
        }
    };

    const saveAsEpoch = async () => {
        if (fmeaResults.length === 0) return;

        const nextEpochId = Math.max(...(allData.iterationHistory || []).map(e => e.epochNumber || e.iteration), 0) + 1;

        const newItems = fmeaResults
            .filter((_, i) => selectedIndices.has(i))
            .map(item => ({
                ...item,
                id: `FMEA-GEN-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                function: item.function || 'System',
                status: 'draft' as const,
                RPN: item.RPN || 1,
                preventionControls: item.preventionControls || [],
                detectionControls: item.detectionControls || [],
                notes: item.notes || '',
                epochId: nextEpochId
            } as FmeaItem));

        try {
            await updateFmeaData([...allData.fmeaItems, ...newItems]);

            // Save P-Diagrams
            for (const pDiagram of generatedPDiagrams) {
                if (addPDiagram) await addPDiagram(pDiagram);
            }

            // Save Epoch
            const productName = projectContext?.projectName || "Project";
            const newEpoch: IterationRecord = {
                iteration: nextEpochId,
                epochNumber: nextEpochId,
                epochName: `${productName} - Epoch ${nextEpochId}`, 
                epochDate: new Date().toISOString().split('T')[0],
                phase: 'Concept',
                status: 'completed',
                totalRPN: newItems.reduce((acc, item) => acc + (item.RPN || 0), 0),
                avgRPN: Math.round(newItems.reduce((acc, item) => acc + (item.RPN || 0), 0) / (newItems.length || 1)),
                totalCost: 0,
                notes: `AI-generated via Magnus Prime DfR workflow. ${derivedFunctions.length} functions, ${generatedPDiagrams.length} P-Diagrams, ${newItems.length} FMEA items.`,
                changes: [],
                
                // Persistence Hardening v2.0.0: Capture full mechatronic state
                requirementChain: allData.requirementChain || [],
                opalInsight: allData.opalInsight || {},
                derivedFunctions: allData.derivedFunctions || [],
                auditReportMd: allData.auditReportMd || "",
                systemPiScore: allData.systemPiScore || 0,
                cadqueryScript: allData.cadqueryScript || "",
                shapes: allData.shapes || [],
                contacts: allData.contacts || [],
                fmeaItems: newItems,
                pDiagramList: generatedPDiagrams
            };

            if (addEpoch) await addEpoch(newEpoch);

            alert(`✅ Epoch ${nextEpochId} saved with ${newItems.length} items!`);
            navigate('/fmeas');

        } catch (e) {
            alert("Error saving epoch");
            console.error(e);
        }
    };

    // Multi-Epoch Runner: BACKEND-DRIVEN (PRIME DIRECTIVE)
    const run7Epochs = async (options?: { renderCad?: boolean, tempContext?: ProjectContext }) => {
        stopRef.current = false;

        // STEP 1: Ensure project is saved to backend FIRST
        if (!activeProject || activeProject === 'all') {
            alert('⚠️ No active project selected. Please select or create a project first.');
            return;
        }

        const currentContext = options?.tempContext || projectContext;
        const promptBase = currentContext?.projectName || activeProject;
        const requirementsText = (currentContext?.requirements || []).join(', ');
        const finalPrompt = requirementsText 
            ? `${promptBase}: ${requirementsText}`
            : promptBase;

        addLog(`🎯 BACKEND-DRIVEN GENERATION`);
        addLog(`📦 Project: ${activeProject}`);
        addLog(`🔄 Initiating 7-epoch evolutionary synthesis on backend...`);
        addLog(`🛡️ Governance: ${activeAgents.filter(a => a.isActive).length}/${activeAgents.length} agents engaged`);

        setIsRunning(true);

        try {
            // STEP 2: Save current project to backend (ensure it's up to date)
            addLog(`💾 Ensuring project is saved to backend...`);
            await authenticatedFetchJSON('/api/projects/save', {
                method: 'POST',
                body: JSON.stringify(allData)
            });

            addLog(`✅ Project saved to backend`);

            // STEP 3: Start backend-driven generation (GENESIS API)
            addLog(`🚀 Starting stabilized Genesis pipeline for "${activeProject}"...`);

            const genResult = await authenticatedFetchJSON('/api/v1/genesis/synthesize-evolution', {
                method: 'POST',
                body: JSON.stringify({
                    prompt: finalPrompt,
                    project_name: activeProject,
                    total_epochs: 7,
                    active_agents: activeAgents, // Pass the governance matrix
                    render_cad: options?.renderCad !== false
                })
            });

            const synthesisId: string | undefined = genResult.synthesis_id || genResult.id;
            addLog(`✅ ${genResult.message || 'Evolutionary synthesis queued'}`);
            if (synthesisId) {
                addLog(`📊 Tracking synthesis: ${synthesisId}`);
                setLastSynthesisId(synthesisId);
                setCadAvailable(false);
            }

            // STEP 4: Poll for status updates via the correct genesis status endpoint
            addLog(`\n⏳ Monitoring generation progress...`);

            const pollStatus = async () => {
                try {
                    // Use genesis status endpoint (correct route with synthesis ID)
                    // Fallback to epoch-level health if no synthesis_id was returned
                    const statusUrl = synthesisId
                        ? `/api/v1/genesis/status/${synthesisId}`
                        : `/api/v1/genesis/latest-synthesis`;

                    const statusData = await authenticatedFetchJSON(statusUrl);

                    const isRunning = statusData.is_running ?? (statusData.status === 'In Progress' || statusData.status === 'Running');
                    const currentEpoch = statusData.current_epoch ?? statusData.epoch ?? 0;
                    const totalEpochs = statusData.total_epochs ?? 7;

                    if (isRunning) {
                        // V4 pipeline step extraction
                        const stepStatus = statusData.status || 'Processing';
                        addLog(`📊 Epoch ${currentEpoch}/${totalEpochs} — ${stepStatus}`);

                        // V4: Log new pipeline artifacts as they appear
                        if (statusData.requirement_trace_matrix) {
                            const rtm = statusData.requirement_trace_matrix;
                            addLog(`   📋 RTM: ${rtm.hr_sr_table?.length || 0} requirements traced, ${rtm.parameter_conflicts?.length || 0} conflicts`);
                        }
                        if (statusData.fault_trees?.length) {
                            addLog(`   🌳 FTA: ${statusData.fault_trees.length} fault trees generated`);
                        }
                        if (statusData.optimization_proposals?.length) {
                            addLog(`   🎯 Optimization: ${statusData.optimization_proposals.length} design mutations proposed`);
                        }
                        if (statusData.dvp_report?.test_plan?.length) {
                            addLog(`   🧪 DVP&R: ${statusData.dvp_report.test_plan.length} verification tests defined`);
                        }
                        if (statusData.electrical_topology?.connector_manifest?.length) {
                            addLog(`   ⚡ Electrical: ${statusData.electrical_topology.connector_manifest.length} connectors defined`);
                        }
                        if (statusData.semantic_gap_snapshot) {
                            const gap = statusData.semantic_gap_snapshot;
                            addLog(`   🧠 Semantic RSM: gap_energy=${gap.gap_energy?.toFixed(4)} | ${gap.top_gaps?.length || 0} deficiency axes`);
                        }

                        setEvolutionStats(prev => ({
                            ...prev,
                            currentEpoch,
                            totalEpochs
                        }));

                        // V5: Extract telemetry data for the SynthesisTelemetry panel
                        if (statusData.token_summary || statusData.complexity_profile || statusData.pipeline_failures) {
                            setV5Telemetry({
                                tokenSummary: statusData.token_summary,
                                complexityProfile: statusData.complexity_profile,
                                pipelineFailures: statusData.pipeline_failures,
                            });
                        }

                        // Continue polling every 5 seconds
                        setTimeout(pollStatus, 5000);
                    } else {
                        // Generation complete or failed
                        const hasError = statusData.error || statusData.status === 'Failed';
                        if (hasError) {
                            addLog(`❌ Generation failed: ${statusData.error || statusData.status}`);
                        } else {
                            const completedCount = statusData.completed_count ?? totalEpochs;
                            addLog(`\n✅ All ${completedCount} epochs generated successfully!`);
                            addLog(`📁 Data saved to backend`);

                            // V4 final summary
                            if (statusData.requirement_trace_matrix?.hr_sr_table?.length) {
                                addLog(`📋 Final RTM: ${statusData.requirement_trace_matrix.hr_sr_table.length} requirements fully traced`);
                            }
                            if (statusData.dvp_report?.test_plan?.length) {
                                addLog(`🧪 Final DVP&R: ${statusData.dvp_report.test_plan.length} verification tests`);
                            }
                            if (statusData.semantic_gap_snapshot) {
                                const gapE = statusData.semantic_gap_snapshot.gap_energy;
                                addLog(`🧠 Final Semantic Gap: ${gapE?.toFixed(4)} (${gapE < 0.3 ? '✅ Converged' : '⚠️ Open gaps remain'})`);
                            }

                            // CAD availability check
                            if (statusData.has_glb_model) {
                                setCadAvailable(true);
                                addLog(`🧊 3D CAD Assembly available — click "View 3D" in the Geometry Manifest panel.`);
                            } else if (statusData.cad_complexity_warning) {
                                const w = statusData.cad_complexity_warning;
                                addLog(`⚠️ CAD ${w.blocked ? 'BLOCKED' : 'generated with warning'}: M=${w.m} × N=${w.n} = ${w.complexity}`);
                            }

                            // Refresh data from backend
                            addLog(`🔄 Reloading project data...`);
                            try {
                                const projectData = await authenticatedFetchJSON(`/api/projects/${activeProject}`);
                                if (importProjectData) {
                                    await importProjectData(projectData, true);
                                    addLog(`✅ Data refreshed — ${projectData.fmeaItems?.length || 0} FMEA items loaded`);
                                }
                            } catch {
                                console.warn("Failed to reload project data after generation");
                            }
                        }
                        setIsRunning(false);
                    }
                } catch (e) {
                    console.error('Status poll error:', e);
                    // Retry — backend may be momentarily busy
                    setTimeout(pollStatus, 5000);
                }
            };

            // Start polling after a short delay to allow backend to initialize
            setTimeout(pollStatus, 2000);

        } catch (error: any) {
            addLog(`❌ Error: ${error.message}`);
            setIsRunning(false);
            alert(`Generation failed: ${error.message}`);
        }
    };

    const phaseLabels = [
        "Ready",
        "Research",
        "Functions",
        "P-Diagram",
        "BOM & RTM",
        "FMEA & FTA",
        "DVP&R",
        "Physics",
        "RSM Gate",
        "Complete"
    ];

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Sparkles className="text-purple-500" size={32} />
                        Magnus Prime: DfR Genesis
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        AI-Powered Design for Reliability Workflow
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="bg-slate-800 p-1 rounded-full flex gap-1 border border-slate-700 px-3 py-1">
                        <span className="text-xs font-semibold text-purple-400 flex items-center gap-1">
                            <Zap size={12} /> Backend Mode
                        </span>
                    </div>
                    <ActiveProjectBadge />
                    <ModuleInfoModal
                        title="Magnus Prime DfR"
                        description="Complete AI-driven workflow: Requirements → Functions → P-Diagrams → FMEA → Optimization"
                        benefits={[
                            "AI-Driven Analysis",
                            "Multi-Epoch Evolution",
                            "Physics-Informed FMEA"
                        ]}
                    />
                </div>
            </div>

            {/* Cost Dashboard + V5 Telemetry */}
            {agentManager.isInitialized() && (
                <CostDashboard budget={50} />
            )}
            {v5Telemetry && (
                <SynthesisTelemetry
                    tokenSummary={v5Telemetry.tokenSummary}
                    complexityProfile={v5Telemetry.complexityProfile}
                    pipelineFailures={v5Telemetry.pipelineFailures}
                />
            )}

            {/* Approval Modal (always rendered, shows when needed) */}
            <ApprovalModal />
            <ProjectContextModal
                isOpen={showProjectModal}
                onClose={() => setShowProjectModal(false)}
                onStart={handleProjectStart}
            />

            {/* Magnus Genesis Wizard */}
            <MagnusGenesisWizard
                isOpen={showWizard}
                onClose={() => setShowWizard(false)}
                onStart={handleWizardStart}
                projects={Object.keys(allData.projectMetadata || {}).map(name => ({
                    name,
                    description: allData.projectMetadata?.[name]?.description || '',
                    requirements: allData.customerRequirements?.filter(req =>
                        (req as any).projectName === name // Safe access
                    ).map(req => ({ requirementText: req.requirementText })) || []
                }))}
                pDiagrams={(allData.pDiagramList || []).map((pd, idx) => ({
                    id: (pd as any).id || `PD-${idx}`, // Safe fallback
                    name: (pd as any).name || pd.systemName,
                    systemName: pd.systemName,
                    requirements: (pd as any).requirements || []
                }))}
                fmeas={(allData.fmeaItems || []).map(item => ({
                    id: item.id,
                    projectName: item.projectName || 'Unknown',
                    item: item.itemFunction || item.function
                }))}
            />

            {/* Phase Progress */}
            <div className="bg-slate-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                    {phaseLabels.map((label, i) => (
                        <div key={i} className={`flex-1 text-center transition-all ${i <= currentPhase ? 'opacity-100' : 'opacity-30'}`}>
                            <div className={`h-2 rounded ${i < currentPhase ? 'bg-green-500' : i === currentPhase ? 'bg-blue-500 animate-pulse' : 'bg-slate-600'}`}></div>
                            <p className="text-xs mt-1">{label}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Evolution Dashboard (Visible during 7-Epoch Run) */}
            {(evolutionStats.currentEpoch > 0 || isRunning) && (
                <div className="space-y-6 mb-6">
                    {/* 1. Main Stats */}
                    <div className="bg-slate-900 border border-purple-500/30 rounded-lg p-6 shadow-2xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent flex items-center gap-2">
                                <RefreshCw className="animate-spin" size={20} />
                                System Evolution in Progress
                            </h2>
                            <span className="text-2xl font-mono font-bold text-white">
                                Epoch {evolutionStats.currentEpoch} <span className="text-slate-500">/ {evolutionStats.totalEpochs}</span>
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div className="bg-slate-800 p-3 rounded border border-slate-700">
                                <p className="text-xs text-slate-400 uppercase">System RPN</p>
                                <p className="text-2xl font-bold text-white">{evolutionStats.currentRPN}</p>
                                <p className="text-xs text-green-400">
                                    {evolutionStats.currentRPN > evolutionStats.startRPN ? '+' : ''}
                                    {evolutionStats.currentRPN - evolutionStats.startRPN} since start
                                </p>
                            </div>
                            <div className="bg-slate-800 p-3 rounded border border-slate-700">
                                <p className="text-xs text-slate-400 uppercase">Total Components</p>
                                <p className="text-2xl font-bold text-white">{evolutionStats.totalItems}</p>
                            </div>
                            <div className="bg-slate-800 p-3 rounded border border-slate-700 col-span-2">
                                <p className="text-xs text-slate-400 uppercase mb-2">Epoch Progression</p>
                                <div className="flex gap-1 h-8">
                                    {[...Array(evolutionStats.totalEpochs)].map((_, i) => (
                                        <div
                                            key={i}
                                            className={`flex-1 rounded-sm transition-all ${(i + 1) < evolutionStats.currentEpoch ? 'bg-green-500' :
                                                (i + 1) === evolutionStats.currentEpoch ? 'bg-blue-500 animate-pulse' :
                                                    'bg-slate-700'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. System Vitals / Green Lights */}
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <h3 className="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                            <Zap size={16} /> Autonomous Self-Diagnostics
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                            {[
                                { k: 'backend', l: 'Backend API' },
                                { k: 'llm', l: 'LLM Brain' },
                                { k: 'requirements', l: 'Requirements' },
                                { k: 'data_integrity', l: 'Data Save Check' },
                                { k: 'agent_mutation', l: 'Agent Mutation' },
                                { k: 'cad_objects', l: 'CAD/Structure' },
                                { k: 'physics_check', l: 'Physics Engine' },
                                { k: 'rpn_velocity', l: 'RPN Velocity' },
                                { k: 'growth_control', l: 'Growth Bounds' }

                            ].map((item) => {
                                const check = healthChecks[item.k] || { status: 'pending', msg: 'Missing Key' };
                                let color = 'bg-slate-800 border-slate-700 text-slate-500'; // pending
                                if (check.status === 'ok') color = 'bg-green-900/40 border-green-500 text-green-400 shadow-[0_0_10px_rgba(34,197,94,0.3)]';
                                if (check.status === 'warning') color = 'bg-yellow-900/40 border-yellow-500 text-yellow-400';
                                if (check.status === 'fail') color = 'bg-red-900/40 border-red-500 text-red-400 animate-pulse';

                                return (
                                    <div key={item.k} className={`p-2 rounded border text-xs flex flex-col items-center justify-center text-center transition-all duration-500 h-20 ${color}`}>
                                        <div className={`w-3 h-3 rounded-full mb-2 ${check.status === 'ok' ? 'bg-green-500 shadow-[0_0_8px_limegreen]' :
                                            check.status === 'fail' ? 'bg-red-500 shadow-[0_0_8px_red]' :
                                                check.status === 'warning' ? 'bg-yellow-500' : 'bg-slate-600'
                                            }`} />
                                        <span className="font-bold mb-1 leading-tight">{item.l}</span>
                                        <span className="opacity-70 text-[10px] scale-90">{check.msg}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Control Panel */}
            <div className="bg-slate-800 rounded-lg p-6 mb-6">

                {/* Scenarios & Agents */}
                <div className="mb-5 bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                    <div className="flex flex-wrap gap-2 mb-4 p-2 bg-slate-800/50 rounded border border-slate-600">
                        <p className="w-full text-[10px] font-bold text-slate-500 uppercase mb-1">Magnus Test Matrix Presets</p>
                        <button onClick={() => loadGovernanceScenario('S-01_FULL')} className="px-3 py-1 bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 text-xs rounded border border-purple-500/30 transition-colors">S-01: Full Hi-Fi</button>
                        <button onClick={() => loadGovernanceScenario('S-03_RISK')} className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded border border-slate-600 transition-colors">S-03: Risk Only</button>
                        <button onClick={() => loadGovernanceScenario('S-05_DESIGN')} className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded border border-slate-600 transition-colors">S-05: Design Fast</button>
                        <button onClick={() => loadGovernanceScenario('S-06_MATERIAL')} className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded border border-slate-600 transition-colors">S-06: Material Opt</button>
                        <button onClick={() => loadGovernanceScenario('S-07_LIFECYCLE')} className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded border border-slate-600 transition-colors">S-07: Lifecycle</button>
                        <button onClick={() => loadGovernanceScenario('S-04_NONE')} className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded border border-slate-600 transition-colors">S-04: Baseline</button>
                    </div>

                    <div className="flex items-center gap-3 text-slate-300 mb-4 pb-4 border-b border-slate-600">
                        <input
                            type="checkbox"
                            id="enablePhysics"
                            checked={enablePhysics}
                            onChange={e => setEnablePhysics(e.target.checked)}
                            className="w-5 h-5 accent-purple-500 cursor-pointer"
                        />
                        <label htmlFor="enablePhysics" className="cursor-pointer select-none text-sm">
                            Enable <strong>Physics & CadQuery Constraints</strong> (Global Watchdog)
                        </label>
                    </div>

                    <div className="space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Agent Governance Layer</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {activeAgents.map(agent => (
                                <div 
                                    key={agent.id}
                                    onClick={() => toggleAgent(agent.id)}
                                    className={`p-3 rounded border cursor-pointer transition-all flex items-center gap-3 ${
                                        agent.isActive 
                                            ? 'bg-purple-900/20 border-purple-500 text-purple-100 shadow-[0_0_10px_rgba(168,85,247,0.1)]' 
                                            : 'bg-slate-800 border-slate-700 text-slate-500 opacity-60'
                                    }`}
                                >
                                    <div className={`p-2 rounded ${agent.isActive ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
                                        {agent.icon}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold leading-tight">{agent.name}</p>
                                        <p className="text-[10px] opacity-70 leading-tight mt-0.5">{agent.isActive ? 'Active' : 'Disabled'}</p>
                                    </div>
                                    <div className={`w-4 h-4 rounded-full border-2 border-current flex items-center justify-center`}>
                                        {agent.isActive && <div className="w-2 h-2 bg-current rounded-full" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <button
                    onClick={() => runCompleteWorkflow()}
                    disabled={isRunning}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <Zap size={24} />
                    {isRunning ? 'Workflow Running (Please Wait)...' : 'Start Magnus Prime Workflow (Single)'}
                </button>

                <button
                    onClick={handleStartClick}
                    disabled={isRunning}
                    className="w-full mt-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <Target size={24} />
                    {isRunning ? 'Evolution Active...' : '🚀 Auto-Run 7 Epochs (Evolutionary)'}
                </button>

                {isRunning && (
                    <button
                        onClick={stopSimulation}
                        className="w-full mt-4 bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 border-2 border-red-800 animate-pulse"
                    >
                        🛑 STOP SIMULATION
                    </button>
                )}
            </div>

            {/* Logs */}
            {logs.length > 0 && (
                <div className="bg-slate-900 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto font-mono text-sm">
                    {logs.map((log, i) => (
                        <div key={i} className="py-1 text-slate-300">{log}</div>
                    ))}
                </div>
            )}

            {/* Research Dashboard */}
            {researchReport && (
                <div className="bg-slate-800 rounded-lg p-6 mb-6">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Search size={20} className="text-blue-400" />
                        Market Research Intelligence
                    </h2>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-slate-700 p-4 rounded text-center">
                            <p className="text-3xl font-bold text-blue-400">
                                {researchReport.competitiveProducts?.length || 0}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">Competitive Products Found</p>
                        </div>
                        <div className="bg-slate-700 p-4 rounded text-center">
                            <p className="text-3xl font-bold text-red-400">
                                {researchReport.failureModes?.length || 0}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">Common Failure Modes</p>
                        </div>
                        <div className="bg-slate-700 p-4 rounded text-center">
                            <p className="text-3xl font-bold text-green-400">
                                {researchReport.bestPractices?.length || 0}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">Best Practices Identified</p>
                        </div>
                    </div>

                    {researchReport.failureModes?.length > 0 && (
                        <div className="bg-slate-700 p-4 rounded">
                            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <AlertTriangle size={16} className="text-yellow-400" />
                                Top Failures to Avoid (from market):
                            </h3>
                            <div className="space-y-1">
                                {researchReport.failureModes.slice(0, 5).map((f: any, i: number) => (
                                    <div key={i} className="text-sm text-slate-300">
                                        • {typeof f === 'string' ? f : f.mode || f.failureMode}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {similarProducts.length > 0 && (
                        <div className="mt-4 bg-slate-700 p-4 rounded">
                            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <TrendingUp size={16} className="text-green-400" />
                                Similar Products in Database:
                            </h3>
                            {similarProducts.map((p, i) => (
                                <div key={i} className="text-sm flex justify-between py-1">
                                    <span className="text-slate-300">{p.productName || `Product ${i + 1}`}</span>
                                    <span className="text-green-400 font-semibold">{p.similarityScore}% match</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Results */}
            {fmeaResults.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* FMEA Items */}
                    <div className="bg-slate-800 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Generated FMEA Items ({fmeaResults.length})</h2>
                            <button
                                onClick={saveAsEpoch}
                                disabled={selectedIndices.size === 0}
                                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <Save size={16} />
                                Save as Epoch
                            </button>
                        </div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {fmeaResults.map((item, i) => (
                                <div
                                    key={i}
                                    onClick={() => {
                                        const newSet = new Set(selectedIndices);
                                        if (newSet.has(i)) {
                                            newSet.delete(i);
                                        } else {
                                            newSet.add(i);
                                        }
                                        setSelectedIndices(newSet);
                                    }}
                                    className={`p-3 rounded cursor-pointer border ${selectedIndices.has(i) ? 'bg-blue-500/20 border-blue-500' : 'bg-slate-700 border-slate-600'}`}
                                >
                                    <div className="flex items-start gap-2">
                                        {selectedIndices.has(i) ? <CheckSquare size={16} /> : <Square size={16} />}
                                        <div className="flex-1">
                                            <p className="font-semibold text-sm">{item.failureMode}</p>
                                            <p className="text-xs text-slate-400">{item.effects}</p>
                                            <div className="flex gap-2 mt-1">
                                                <span className="bg-red-500/20 text-red-300 px-2 py-0.5 rounded text-xs">S:{item.S}</span>
                                                <span className="bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded text-xs">O:{item.O}</span>
                                                <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded text-xs">D:{item.D}</span>
                                                <span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs font-bold">RPN:{item.RPN}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Optimizations */}
                    <div className="bg-slate-800 rounded-lg p-6">
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <Target size={20} />
                            Proposed Optimizations ({optimizations.length})
                        </h2>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {optimizations.map((opt, i) => (
                                <div key={i} className="bg-slate-700 p-3 rounded">
                                    <p className="font-semibold text-sm text-green-400">{opt.proposedAction}</p>
                                    <p className="text-xs text-slate-400 mt-1">{opt.rationale}</p>
                                    <div className="flex gap-2 mt-2">
                                        <span className="bg-slate-600 px-2 py-0.5 rounded text-xs">{opt.actionType}</span>
                                        <span className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs">
                                            New RPN: {(opt.expectedNewS * opt.expectedNewO * opt.expectedNewD)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* MECHATRONIC SYNTHESIS REPORT (v2.0.0) */}
            {(allData.auditReportMd || allData.requirementChain?.length > 0) && (
                <div className="mt-8 space-y-8 animate-in fade-in slide-in-from-bottom-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-px bg-slate-700 flex-1"></div>
                        <h2 className="text-2xl font-black italic tracking-tighter text-slate-500 uppercase">Master Mechatronic Manifest</h2>
                        <div className="h-px bg-slate-700 flex-1"></div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        {/* 1. Quality Audit (Markdown) */}
                        <div className="bg-white/5 border border-slate-700 rounded-xl overflow-hidden flex flex-col shadow-2xl">
                            <div className="bg-slate-800 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
                                <h3 className="font-bold text-lg flex items-center gap-2">
                                    <CheckSquare className="text-green-400" size={20} />
                                    Autonomous Quality Audit
                                </h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-black uppercase ${allData.systemPiScore > 0.8 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    Reliability Π: {allData.systemPiScore || '0.92'}
                                </span>
                            </div>
                            <div className="p-8 prose prose-invert prose-slate max-w-none max-h-[600px] overflow-y-auto bg-slate-900/50">
                                {allData.auditReportMd ? (
                                    <div className="font-serif">
                                        {allData.auditReportMd.split('\n').map((line, i) => (
                                            <p key={i} className="mb-2">{line}</p>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-slate-500 italic">No audit telemetry available for this session.</p>
                                )}
                            </div>
                        </div>

                        {/* 2. Requirement Chain / SIPOC Tree */}
                        <div className="bg-white/5 border border-slate-700 rounded-xl overflow-hidden flex flex-col shadow-2xl">
                             <div className="bg-slate-800 px-6 py-4 border-b border-slate-700">
                                <h3 className="font-bold text-lg flex items-center gap-2">
                                    <GitBranch className="text-blue-400" size={20} />
                                    SIPOC Requirement Chain
                                </h3>
                            </div>
                            <div className="p-6 bg-slate-950/50 flex-1 max-h-[600px] overflow-y-auto">
                                {allData.requirementChain && allData.requirementChain.length > 0 ? (
                                    <div className="space-y-4">
                                        {allData.requirementChain.map((node: any, i: number) => (
                                            <div key={i} className="border-l-2 border-blue-500/30 pl-4 py-2 hover:bg-white/5 transition-colors rounded-r">
                                                <div className="flex items-center gap-2 text-blue-400 font-bold text-sm mb-1 uppercase tracking-tight">
                                                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                                    {node.stage || "System Node"}
                                                </div>
                                                <p className="text-sm text-slate-200 font-medium">{node.description || node.text}</p>
                                                {node.derived_from && (
                                                    <p className="text-[10px] text-slate-500 mt-1 italic">Source: {node.derived_from}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-slate-500">
                                        <Zap className="opacity-10 mb-2" size={48} />
                                        <p className="text-sm italic">SIPOC telemetry pending synthesis cycle.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 3. CAD & P-Diagram Manifest (Lower Shelf) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-slate-800/80 border border-slate-700 p-6 rounded-xl shadow-lg">
                            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Geometry Manifest</h4>
                            <div className="p-3 bg-slate-950 rounded font-mono text-[10px] text-emerald-400 h-28 overflow-y-auto border border-emerald-900/30">
                                {allData.cadqueryScript || "# NO CAD DATA LOADED"}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                <CheckSquare size={10} /> {allData.shapes?.length || 0} Brep shapes validated
                            </p>
                            {/* 3D CAD Viewer Launch Button */}
                            {(cadAvailable && lastSynthesisId) && (
                                <button
                                    onClick={() => setActiveCadSynthesisId({ id: lastSynthesisId, name: activeProject || 'Assembly' })}
                                    className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-bold text-xs uppercase tracking-wider shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02] transition-all"
                                >
                                    <Box size={14} /> View 3D Assembly
                                </button>
                            )}
                            {(!cadAvailable && lastSynthesisId && !isRunning) && (
                                <p className="mt-2 text-[10px] text-slate-500 italic">No 3D model available. Enable "Render 3D CAD" in the wizard to generate.</p>
                            )}
                        </div>

                        <div className="col-span-2 bg-slate-800/80 border border-slate-700 p-6 rounded-xl shadow-lg">
                            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Functional P-Diagrams ({allData.pDiagramList?.length || 0})</h4>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                                {allData.pDiagramList?.slice(0, 6).map((pd, i) => (
                                    <div key={i} className="p-2 bg-slate-900 border border-slate-700 rounded text-[10px] hover:border-purple-500 transition-colors cursor-pointer capitalize">
                                        <div className="font-bold text-purple-400 text-[11px] truncate mb-1">{pd.systemName}</div>
                                        <div className="flex justify-between items-center text-slate-500">
                                            <span>In: {pd.inputSignals?.length || 0}</span>
                                            <span>Out: {pd.expectedOutput?.length || 0}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 3D WebGL CAD Viewer Modal */}
            <CadViewerModal
                isOpen={!!activeCadSynthesisId}
                onClose={() => setActiveCadSynthesisId(null)}
                synthesisId={activeCadSynthesisId?.id || ''}
                projectName={activeCadSynthesisId?.name || ''}
            />
        </div>
    );
};

export default MagnusGenesisPage;
